"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useSyncExternalStore } from "react";
const STORAGE_KEY = "vaw-search-history";
const MAX_ITEMS = 10;

/**
 * Subscribers for useSyncExternalStore to react to localStorage changes.
 */
const listeners = new Set<() => void>();
function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}
function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

// Cached snapshot to avoid infinite loops — useSyncExternalStore requires
// getSnapshot to return the same reference if the data hasn't changed.
const EMPTY: string[] = [];
let cachedSnapshot: string[] = EMPTY;
let cachedRaw: string | null = null;
function getSnapshot(): string[] {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedSnapshot;
    cachedRaw = raw;
    if (!raw) {
      cachedSnapshot = EMPTY;
      return EMPTY;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      cachedSnapshot = EMPTY;
      return EMPTY;
    }
    cachedSnapshot = parsed.filter((item): item is string => typeof item === "string");
    return cachedSnapshot;
  } catch {
    cachedSnapshot = EMPTY;
    return EMPTY;
  }
}
function getServerSnapshot(): string[] {
  return EMPTY;
}
function saveHistory(items: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    emitChange();
  } catch {
    // localStorage unavailable
  }
}

/**
 * Hook for managing search history in localStorage.
 * Uses useSyncExternalStore for cross-component reactivity.
 */
export function useSearchHistory() {
  const $ = _c(2);
  const history = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const addSearch = _temp;
  const removeSearch = _temp2;
  const clearHistory = _temp3;
  let t0;
  if ($[0] !== history) {
    t0 = {
      history,
      addSearch,
      removeSearch,
      clearHistory
    };
    $[0] = history;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp3() {
  saveHistory([]);
}
function _temp2(query_0) {
  const current_0 = getSnapshot();
  const updated_0 = current_0.filter(item_0 => item_0.toLowerCase() !== query_0.toLowerCase());
  saveHistory(updated_0);
}
function _temp(query) {
  const trimmed = query.trim();
  if (!trimmed) {
    return;
  }
  const current = getSnapshot();
  const filtered = current.filter(item => item.toLowerCase() !== trimmed.toLowerCase());
  const updated = [trimmed, ...filtered].slice(0, MAX_ITEMS);
  saveHistory(updated);
}